import {useEffect, useMemo} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {useApiRequest} from "../../../../../common/hooks/useApiRequest";
import {useSnackbar} from "../../../../../common/context/SnackbarContext";
import {useAudio} from "../../../../../common/context/AudioContext";
import {useDeck} from "../../../../deck-management/context/DeckContext";
import {BasicWordWithId, PartOfSpeech, WordFullInfo, WordWithId} from "../../../../../common/types/words";
import {getAudioForSource} from "../../../../../common/utils/audioUtils";

export type UseWordInfoViewWord = WordWithId | BasicWordWithId;

export interface UseWordInfoViewArgs {
    word: UseWordInfoViewWord;
}

export const useWordInfoView = ({word}: UseWordInfoViewArgs) => {

    const queryClient = useQueryClient();
    const {currentDeck, ready: deckReady} = useDeck();
    const {getRequest} = useApiRequest();
    const {showSnackbar} = useSnackbar();
    const {play} = useAudio();

    const isBasicWordInfo = !("translations" in word);

    const {data: fullWordInfo, isPending, isError} = useQuery({
        queryKey: ["word-info", word.wordId],
        queryFn: () => getRequest<WordFullInfo>(`decks/${currentDeck?.deckId}/words/${word.wordId}`),
        enabled: deckReady,
    });

    const basicWordFallback = useMemo<WordWithId | null>(() => {
        if (!isBasicWordInfo) {
            return null;
        }

        return {
            wordId: String(word.wordId),
            lemma: word.lemma,
            translations: [word.translation],
            exampleSentences: [],
            partOfSpeech: PartOfSpeech.Unknown,
            userTranslations: [],
            morphology: null,
            resources: null,
        };
    }, [isBasicWordInfo, word]);

    const usedWord = useMemo<WordWithId>(() => {
        if (fullWordInfo) {
            return fullWordInfo;
        }

        if (isBasicWordInfo) {
            return basicWordFallback ?? (word as unknown as WordWithId);
        }

        return word as unknown as WordWithId;
    }, [basicWordFallback, fullWordInfo, isBasicWordInfo, word]);

    const audio = useMemo(() => {
        return getAudioForSource(usedWord.resources?.lemma);
    }, [usedWord]);

    const sentenceAudio = useMemo(() => {
        return getAudioForSource(usedWord.resources?.sentence);
    }, [usedWord]);

    const isWordLearned = useMemo(() => {
        return !!fullWordInfo?.learnedTime && new Date(fullWordInfo.learnedTime).getTime() !== 0;
    }, [fullWordInfo]);

    useEffect(() => {
        if (isError) {
            showSnackbar("Could not get full info", "error");
        }
    }, [isError, showSnackbar]);

    const handlePlayAudio = (audioToPlay: HTMLAudioElement | null) => {
        if (audioToPlay) {
            play(audioToPlay);
            return;
        }

        showSnackbar("Playback error");
    };

    return {
        queryClient,
        currentDeck,
        showSnackbar,
        fullWordInfo,
        isPending,
        isError,
        isBasicWordInfo,
        usedWord,
        audio,
        sentenceAudio,
        isWordLearned,
        handlePlayAudio,
    };
};
